import { Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfigColumns } from 'datatables.net';
import { DataTableDirective } from 'angular-datatables';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { Subject, Subscription } from 'rxjs';
import { ColumnData, DataTableRequest, DataTableResult } from 'angular-core-src';
import { LanguageService } from '../../../services/language.service';
import { ModalOptions } from 'ngx-bootstrap/modal';

@Component({
    template: '',
    standalone: false,
})
export abstract class BaseComponent {
    private isSpinnerVisible: boolean = false;

    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    dtOptions: ADTSettings = {};
    dtTrigger: Subject<ADTSettings>;
    showTable: boolean = true;

    public datatableRequest: DataTableRequest;
    public searchValue: string;

    public languageSubscription: Subscription;
    public languageUrl: string;
    private currentLanguage: string = '';

    constructor(
        protected spinnerService: NgxSpinnerService,
        protected translate?: TranslateService,
        protected languageService?: LanguageService
    ) {}

    public dataTableOnInit() {
        this.dtTrigger = new Subject<ADTSettings>();
        setTimeout(() => {
            this.dtOptions = this.getDtOptions();
        });

        this.languageSubscription = this.languageService.language$.subscribe(value => {
            if (this.currentLanguage !== value) {
                this.currentLanguage = value;
                this.dtOptions = this.getDtOptions();
                this.reinitializeDataTable();
            }
        });
    }

    public dataTableAfterViewInit() {
        setTimeout(() => {
            this.dtOptions.columns.forEach(column => {
                if (column && column.title) {
                    column.title = this.translate.instant(column.title);
                }
            });
            this.dtTrigger.next(this.dtOptions);
        });
    }

    public dataTableOnDestroy() {
        this.dtTrigger.unsubscribe();
        this.languageSubscription.unsubscribe();
    }

    // override
    public getDtOptions() {
        return this.dtOptions;
    }

    public transformToColumnData(columns: ConfigColumns[]): ColumnData[] {
        return columns.map(column => ({
            data: column.data + '',
            name: column.title,
            searchable: column.searchable ?? false,
            orderable: column.orderable ?? true,
        }));
    }

    public refreshDataTable() {
        if (!this.dtElement) {
            return;
        }
        this.dtElement?.dtInstance?.then((dtInstance: any) => {
            dtInstance.ajax.reload();
        });
    }

    public reinitializeDataTable() {
        if (!this.dtElement) {
            return;
        }
        this.dtElement?.dtInstance?.then((dtInstance: any) => {
            dtInstance.destroy();
            this.dtTrigger.unsubscribe();
            this.showTable = false;

            this.dtTrigger = new Subject<ADTSettings>();
            setTimeout(() => {
                this.showTable = true;
                setTimeout(() => {
                    this.dtOptions.columns.forEach(column => {
                        if (column && column.title) {
                            column.title = this.translate.instant(column.title);
                        }
                    });
                    this.dtTrigger.next(this.dtOptions);
                });
            });
        });
    }
    public getTableData(res: DataTableResult) {
        return {
            draw: res.draw,
            recordsTotal: res.recordsTotal,
            recordsFiltered: res.recordsFiltered,
            data: res.data,
        };
    }

    public getEmptyTableData() {
        let emptyTableData: DataTableResult = {
            draw: 1,
            recordsTotal: 0,
            recordsFiltered: 0,
            data: [],
        };

        return emptyTableData;
    }
    public getDefaultModalOptions(title: string, item?: any): any {
        let modalOptions: ModalOptions = {
            class: 'modal-lg',
            initialState: {
                title: title,
            },
        };
        if (item) {
            modalOptions.initialState['item'] = item;
        }
        return modalOptions;
    }

    public handleSpinner(promise: Promise<any>): Promise<any> {
        if (!this.isSpinnerVisible) {
            this.isSpinnerVisible = true;
            this.spinnerService.show();
        }
        promise
            .then(res => {
                this.spinnerService.hide();
                this.isSpinnerVisible = false;
            })
            .catch(() => {
                this.spinnerService.hide();
                this.isSpinnerVisible = false;
            });

        return promise;
    }

    protected setImgSrc(elementId: string, textureUrl: string) {
        var img = <HTMLImageElement>document.getElementById(elementId);
        if (img) img.src = textureUrl;
    }

    protected setEmptyImgSrc(elementId: string) {
        this.setImgSrc(elementId, '/assets/img/red_x-80x80.png');
    }

    protected setEmptyFileInputValue(elementId: string) {
        var textureSelectorInput = <HTMLInputElement>document.getElementById(elementId);
        if (textureSelectorInput) textureSelectorInput.value = '';
    }

    protected isImageFileExtensionGood(file: File) {
        const allowedExtensions = ['jpg', 'jpeg', 'gif', 'png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        return fileExtension ? allowedExtensions.includes(fileExtension) : false;
    }

    protected isFileSizeGood(file: File) {
        const maxFileSize = 10 * 1024 * 1024; // 10 MB
        return file.size <= maxFileSize;
    }
}
